import axios from 'axios'

// Singleton service para catálogos CAD (incidentes CNI + tipos de dispositivo).
// Los catálogos se filtran por `center` (typicamente user.attributes.cad_center).
// Caché simple en memoria por center: una vez cargados, sucesivas llamadas
// devuelven la promesa cacheada. Refrescar con .reset().
class CadCatalogsService {
  constructor() {
    if (typeof CadCatalogsService.instace === 'object') return CadCatalogsService.instace
    CadCatalogsService.instace = this
    this._incidentCache = new Map()
    this._deviceCache = new Map()
    this._clearancesCache = new Map()
    this._cancelCodesCache = new Map()
  }

  // GET /api/v2/cad-catalogs/cni-incident-catalog?center=<center>
  // Devuelve array de { incidentCNIId, incidentCNIName }.
  async getIncidentTypes(center) {
    if (!center) return []
    if (this._incidentCache.has(center)) return this._incidentCache.get(center)
    const p = axios.get('/api/v2/cad-catalogs/cni-incident-catalog', {
      params: { center },
    }).then((res) => Array.isArray(res.data) ? res.data : [])
      .catch(() => [])
    this._incidentCache.set(center, p)
    return p
  }

  // GET /api/v2/cad-catalogs/tipos-dispositivo-catalog?center=<center>
  // Devuelve array de { idTipoDispositivo, nombreTipoDispositivo }.
  async getDeviceTypes(center) {
    if (!center) return []
    if (this._deviceCache.has(center)) return this._deviceCache.get(center)
    const p = axios.get('/api/v2/cad-catalogs/tipos-dispositivo-catalog', {
      params: { center },
    }).then((res) => Array.isArray(res.data) ? res.data : [])
      .catch(() => [])
    this._deviceCache.set(center, p)
    return p
  }

  // POST /api/v2/cad-incidents/create-incident
  // Forma del body alineada al legacy NewIncidentForm.vue.
  async createIncident(payload) {
    const response = await axios.post('/api/v2/cad-incidents/create-incident', payload)
    return response.data
  }

  // GET /api/v2/cad-catalogs/unit-status?center=<center>
  // Devuelve array de { identifier, statdesc, active, ... } ordenado por
  // identifier numérico. El consumidor (panel de Despacho) suele filtrarlo
  // luego por identifier === '1' (Despacho) para el dropdown del modal.
  async getUnitStatusCatalog(center) {
    if (!center) return []
    try {
      const res = await axios.get('/api/v2/cad-catalogs/unit-status', { params: { center } })
      const data = Array.isArray(res.data) ? res.data : []
      return data.filter((s) => s.active).sort((a, b) => {
        const na = Number(a.identifier)
        const nb = Number(b.identifier)
        const isNumA = !Number.isNaN(na)
        const isNumB = !Number.isNaN(nb)
        if (isNumA && isNumB) return na - nb
        if (isNumA) return -1
        if (isNumB) return 1
        return String(a.identifier).localeCompare(String(b.identifier))
      })
    } catch (err) {
      return []
    }
  }

  // GET /api/v2/cad-catalogs/units-catalog-by-corporacion
  //   ?center=<center>&params={corporacion, available:true}
  // Devuelve unidades disponibles para asignar al incidente, en una
  // corporación específica. El backend espera `params` como objeto JSON
  // dentro del query string (legacy quirk).
  async getUnitsByCorporacion({ center, corporacion, available = true }) {
    if (!center || !corporacion) return []
    try {
      const res = await axios.get('/api/v2/cad-catalogs/units-catalog-by-corporacion', {
        params: { center, params: { corporacion, available } },
      })
      return Array.isArray(res.data) ? res.data : []
    } catch (err) {
      return []
    }
  }

  // POST /api/v2/cad-incidents/assign-unit
  // Payload: { center, corporacion, unit, status, folioEmergencia }
  // status es el `identifier` del unit-status catalog (e.g. '1' = Despacho).
  async assignUnit(payload) {
    const response = await axios.post('/api/v2/cad-incidents/assign-unit', payload)
    return response.data
  }

  // POST /api/v2/cad-incidents/change-unit-status
  // Cambio de estado de una unidad despachada (state machine forward-only:
  // Despacho → Llegada → Libre). Payload:
  // { incidentId, corporacion, unit, status, center, folioEmergencia }
  async changeUnitStatus(payload) {
    const response = await axios.post('/api/v2/cad-incidents/change-unit-status', payload)
    return response.data
  }

  // GET /api/v2/cad-catalogs/clearances?center=<center>
  // Catálogo de "clearances" (códigos de cierre) para el dialog Cerrar
  // incidente. Items con { desc, identifier }. Cacheado por center.
  async getClearances(center) {
    if (!center) return []
    if (this._clearancesCache.has(center)) return this._clearancesCache.get(center)
    const p = axios.get('/api/v2/cad-catalogs/clearances', { params: { center } })
      .then((res) => Array.isArray(res.data) ? res.data : [])
      .catch(() => [])
    this._clearancesCache.set(center, p)
    return p
  }

  // GET /api/v2/cad-catalogs/call-cancel?center=<center>
  // Catálogo de códigos de cancelación para el dialog Cancelar incidente.
  // Items con { desc, identifier }. Cacheado por center.
  async getCancelCodes(center) {
    if (!center) return []
    if (this._cancelCodesCache.has(center)) return this._cancelCodesCache.get(center)
    const p = axios.get('/api/v2/cad-catalogs/call-cancel', { params: { center } })
      .then((res) => Array.isArray(res.data) ? res.data : [])
      .catch(() => [])
    this._cancelCodesCache.set(center, p)
    return p
  }

  // POST /api/v2/cad-incidents/cancel-incident
  // Cancela el incidente para una corporación específica con un cancelCode.
  // Payload: { incidentId, corporacion, reason, cancelCode, center }
  async cancelIncident(payload) {
    const response = await axios.post('/api/v2/cad-incidents/cancel-incident', payload)
    return response.data
  }

  // POST /api/v2/cad-incidents/close-incident
  // Cierra el incidente para una corporación específica con un clearance.
  // Payload: { incidentId, corporacion, clearance, center }
  async closeIncident(payload) {
    const response = await axios.post('/api/v2/cad-incidents/close-incident', payload)
    return response.data
  }

  reset() {
    this._incidentCache.clear()
    this._deviceCache.clear()
    this._clearancesCache.clear()
    this._cancelCodesCache.clear()
  }
}

const cadCatalogsService = new CadCatalogsService()
export default cadCatalogsService
